import { motion } from "framer-motion";
import { ShieldCheck, Globe, Award, Flag } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const TEAM_PHOTO =
  "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

// Life at MNH gallery — 5 culture/engagement photos
const GALLERY_PHOTOS = [
  // Engagement collage (high-fives, brainstorm, yoga, tug-of-war, jenga, celebration)
  "https://customer-assets.emergentagent.com/job_premium-home-decor-13/artifacts/eveylfpr_Employee-Engagement-Activities.webp",
  // Gears + hands — one team, one mission
  "https://customer-assets.emergentagent.com/job_premium-home-decor-13/artifacts/x3yy8yqx_360_F_400155626_Gga4YgM9PpyBBNDu6Ce7zPNOWGK2C3OI.jpg",
  // Diverse collaborative meeting
  "https://customer-assets.emergentagent.com/job_premium-home-decor-13/artifacts/yihj1u7o_collaborative-process-indian-business-mans-brainstorming-meeting-officediverse-team-young-people-dressed-suits-cooperating-developing-common-project_627829-13765.avif",
  // Vision 2030 + construction in hand
  "https://customer-assets.emergentagent.com/job_premium-home-decor-13/artifacts/9lz52t7k_images.jpg",
  // Outdoor team-building event (red & white shirts)
  "https://customer-assets.emergentagent.com/job_premium-home-decor-13/artifacts/teo0awik_IMG_0633_Edited-1-scaled.jpg",
];

// Bento spans — 12-col grid
const GALLERY_SPANS = [
  "md:col-span-7 md:row-span-2",   // Engagement (large)
  "md:col-span-5",                 // Gears
  "md:col-span-5",                 // Collaboration
  "md:col-span-6",                 // Vision 2030
  "md:col-span-6",                 // Team building
];
const GALLERY_HEIGHTS = [
  "h-[460px]",
  "h-[220px]",
  "h-[220px]",
  "h-[280px]",
  "h-[280px]",
];

const PILLAR_ICONS = [ShieldCheck, Globe, Award, Flag];

export default function Workforce() {
  const { t, dir } = useI18n();
  const isRtl = dir === "rtl";
  const w = t.workforce;

  return (
    <section
      id="workforce"
      className="bg-background border-y border-border"
      data-testid="workforce-section"
    >
      <div className="container-page section-y">
        {/* Top: heading + photo + intro copy */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Photo */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 relative"
            data-testid="workforce-photo"
          >
            <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
              <img
                src={TEAM_PHOTO}
                alt="MNH Operations Team"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

              {/* Floating caption tag */}
              <div
                className={`absolute bottom-5 ${isRtl ? "right-5" : "left-5"} bg-secondary/85 backdrop-blur-md text-secondary-foreground px-5 py-3 border border-white/10`}
              >
                <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-semibold">
                  Operations
                </div>
                <div className="mt-0.5 text-sm font-bold">{w.photoCaption}</div>
              </div>

              {/* Top-right badge */}
              <div
                className={`absolute top-5 ${isRtl ? "left-5" : "right-5"} bg-primary text-primary-foreground px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.22em]`}
              >
                1,000+ Strong
              </div>
            </div>

            {/* Decorative gold frame */}
            <div className={`hidden md:block absolute -bottom-5 h-24 w-1/3 border border-primary pointer-events-none ${isRtl ? "-left-5" : "-right-5"}`} />
          </motion.div>

          {/* Heading + copy */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-primary" />
              <span className="overline">{w.eyebrow}</span>
            </div>
            <h2 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1]">
              {w.heading1} <br />
              <span className="text-primary">{w.heading1Accent}</span>
            </h2>
            <p className="mt-7 text-lg text-foreground/80 leading-relaxed">
              {w.lead}
            </p>
            <div className="mt-6 space-y-4 text-muted-foreground leading-relaxed">
              <p>{w.paragraph1}</p>
              <p>{w.paragraph2}</p>
            </div>
          </motion.div>
        </div>

        {/* Stat strip */}
        <div
          className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border"
          data-testid="workforce-stats"
        >
          {w.stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              className="bg-background p-7 lg:p-9"
              data-testid={`workforce-stat-${i}`}
            >
              <div className="text-4xl lg:text-5xl font-black tracking-tight">
                {s.value}
              </div>
              <div className="mt-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Pillars: 4 capability cards */}
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="workforce-pillars">
          {w.pillars.map((p, i) => {
            const Icon = PILLAR_ICONS[i % PILLAR_ICONS.length];
            return (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                className="group relative bg-card border border-border p-7 hover:border-primary transition-colors"
                data-testid={`workforce-pillar-${i}`}
              >
                <div className="flex items-start justify-between">
                  <div className="h-11 w-11 bg-secondary text-primary flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-1 bg-primary/10 text-primary border border-primary/30">
                    {p.tag}
                  </span>
                </div>
                <h3 className="mt-7 text-xl font-bold leading-tight">{p.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  {p.description}
                </p>
                <div className="mt-6 h-px w-10 bg-primary group-hover:w-24 transition-all duration-500" />
              </motion.article>
            );
          })}
        </div>

        {/* Life at MNH gallery */}
        <div className="mt-24" data-testid="workforce-gallery">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <span className="overline">{w.galleryEyebrow}</span>
              <h3 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.1]">
                {w.galleryTitle}
              </h3>
            </div>
            <p className="max-w-md text-muted-foreground leading-relaxed">
              {w.galleryDescription}
            </p>
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-12 gap-4 auto-rows-[220px]"
            data-testid="workforce-gallery-grid"
          >
            {GALLERY_PHOTOS.map((p, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                className={`relative group overflow-hidden border border-border ${GALLERY_SPANS[i]} ${GALLERY_HEIGHTS[i]}`}
                data-testid={`workforce-gallery-${i}`}
              >
                <img
                  src={p}
                  alt={w.galleryCaptions[i].caption}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                <figcaption
                  className={`absolute bottom-0 left-0 right-0 p-5 sm:p-6 ${
                    isRtl ? "text-right" : "text-left"
                  }`}
                >
                  <div className="text-[10px] tracking-[0.25em] uppercase text-primary font-semibold">
                    {w.galleryCaptions[i].tag}
                  </div>
                  <div className="mt-1.5 text-white text-base sm:text-lg font-bold leading-snug">
                    {w.galleryCaptions[i].caption}
                  </div>
                  <div className="mt-3 h-px w-10 bg-primary group-hover:w-24 transition-all duration-500" />
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
